import React, { useEffect, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Card,
  Container,
  Typography,
  Stack,
  Grid,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  Chip,
  Tooltip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { vehicleCodeChanger } from "../helpers/vehicleCodeChanger";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FilterListIcon from "@mui/icons-material/FilterList"; // Ícone do botão
import CloseIcon from "@mui/icons-material/Close"; // Ícone de fechar modal
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  AUTOMATIC,
  BIG,
  CABRIO,
  DESKS,
  DIESEL,
  ELETRIC,
  MANUAL,
  MEDIUM,
  MINIVAN,
  PETROL,
  PREMIUM,
  SCOOTERS,
  SMALL,
  SUVS,
  VAN,
  VEHICLES,
} from "../helpers/Constants";
import InformationModal from "../Components/Modal/InformationModal";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import ErrorModal from "../Components/Modal/ErrorModal";
import { useForm } from "react-hook-form";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocation } from "react-router";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Filter } from "@mui/icons-material";
import FiltersDropdown from "../Components/FiltersDropdown";
import CarCategorySelector from "../helpers/CarCategorySelector";
import FiltersSidebar from "../Components/FilterSideBar";
import { getVehicleImageUrl } from "../helpers/getVehicleImageUrl";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1C2541",
    },
    secondary: {
      main: "#EE8013",
    },
    terceary: {
      main: "#F4F4F4",
    },
  },
});

const styles = {
  background: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    width: "100%",
  },
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(type, vehicleTypeFilter, theme) {
  return {
    fontWeight: vehicleTypeFilter.includes(type)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

function ChooseVehicle({
  reservationInfo,
  avaiabilities,
  setWasCarChoosed,
  setVehicleChoosedDetails,
  pickUpDesk,
  dropOffDesk,
  totalDays,
  choosedCar,
  wasCarChoosed,
  pageChangedFromOptional,
  setTotalDays,
  setReservationDetails,
  setVehicleAvaiabilities,
  setPickUpDesk,
  setDropOffDesk,
}) {
  const { t } = useTranslation();
  const handleReservationOptionals = (
    vehicleModel,
    vehicleCode,
    fuelType,
    pictureUrl,
    transmissionType,
    doorCount,
    passengerQuantity,
    totalPrice,
    SCDW,
    GTW,
    extraFees,
    extraEquipments,
    coverages,
    updatedTotalDays,
  ) => {
    setWasCarChoosed(true);
    setVehicleChoosedDetails({
      vehicleModel,
      vehicleCode,
      fuelType,
      pictureUrl,
      transmissionType,
      doorCount,
      passengerQuantity,
      totalPrice,
      SCDW,
      GTW,
      extraFees,
      extraEquipments,
      coverages,
      updatedTotalDays,
    });
  };

  const data = useLocation();
  const rentalDetails = data.state;

  const [currentAvaiabilities, setCurrentAvaiabilities] = useState(
    rentalDetails?.avaiabilities ?? null,
  );
  const [updateAvaiabilities, setUpdateAvaiabilites] = useState(false);

  const [activeFilters, setActiveFilters] = useState({
    transmission: [], // Ex: ['Automatic', 'Manual']
    fuel: [], // Ex: ['Petrol', 'Diesel']
    category: [], // Ex: ['Small', 'Suv', 'Premium']
  });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [cars, setCars] = useState([]);
  const [openErrorModal, setOpenErrorModal] = useState(false); // State to control modal visibility
  const [openInformationModal, setOpenInformationModal] = useState(false); // State to control modal visibility
  const [openAvaiabilityInformationModal, setOpenAvaiabilityInformationModal] =
    useState(false); // State to control modal visibility
  const [wasModalShowed, setWasModalShowed] = useState(false);
  const [updatedTotalDays, setUpdatedTotalDays] = useState(totalDays);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dbFleet, setDbFleet] = useState({});

  useEffect(() => {
    fetch(`https://api.whynotcarrental.com/api/fleet`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const map = {};
          data.forEach((v) => { map[v.code] = v; });
          setDbFleet(map);
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setVehicleTypeFilter(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(", ") : value,
    );
  };

  const handleClearFilters = () => {
    setActiveFilters({
      fuel: [],
      transmission: [],
      category: [],
    });
  };

  useEffect(() => {
    const vehiclesAvaiable =
      currentAvaiabilities?.flatMap((innerArr) => innerArr?.VehAvail) ?? null;

    const codeCounts = {};

    for (const obj of vehiclesAvaiable) {
      const code = obj?.VehAvailCore?.Vehicle?.VehMakeModel?.Code; // assuming the code property is named "code"
      if (codeCounts[code]) {
        codeCounts[code]++;
      } else {
        codeCounts[code] = 1;
      }
    }

    if (vehiclesAvaiable === undefined) {
      setCars([]);
    } else {
      setCars(
        [
          ...new Set(
            vehiclesAvaiable?.map(
              (obj) => obj?.VehAvailCore?.Vehicle?.VehMakeModel?.Code,
            ),
          ),
        ]?.map((code) =>
          vehiclesAvaiable?.find(
            (obj) => obj?.VehAvailCore?.Vehicle?.VehMakeModel?.Code === code,
          ),
        ),
      );
    }
  }, [currentAvaiabilities]);

  useEffect(() => {
    setVehicles(
      cars !== null
        ? cars?.map((vehicle) => ({
            vehicleModel: vehicle?.VehAvailCore?.Vehicle?.VehMakeModel?.Name,
            vehicleCode: vehicle?.VehAvailCore?.Vehicle?.VehMakeModel?.Code,
            fuelType: vehicle?.VehAvailCore?.Vehicle?.FuelType,
            pictureUrl: vehicle?.VehAvailCore?.Vehicle?.PictureURL,
            transmissionType: vehicle?.VehAvailCore?.Vehicle?.TransmissionType,
            doorCount: vehicle?.VehAvailCore?.Vehicle?.VehType?.DoorCount,
            passengerQuantity:
              vehicle?.VehAvailCore?.Vehicle?.PassengerQuantity,
            totalPrice: vehicle?.VehAvailCore?.TotalCharge?.RateTotalAmount,
            SCDW: vehicle?.VehAvailInfo?.PricedCoverages?.PricedCoverage[1]
              ?.Charge?.Amount,
            GTW: vehicle?.VehAvailInfo?.PricedCoverages?.PricedCoverage[2]
              ?.Charge?.Amount,
            extraFees: vehicle?.VehAvailCore?.Fees?.Fee,
            extraEquipments: vehicle?.VehAvailCore?.PricedEquips?.PricedEquip,
            coverages: vehicle?.VehAvailInfo?.PricedCoverages?.PricedCoverage,
          }))
        : null,
    );
  }, [cars, vehicles]);

  //(dayjs(pickUpDate?.$d).format("DD/MM/YYYY")

  function vehicleType(code) {
    if (SCOOTERS.includes(code)) return t("scooter");
    if (BIG.includes(code)) return t("large");
    if (SUVS.includes(code)) return t("suv");
    if (SMALL.includes(code))
      return code === "NCMV"
        ? `${t("small")} ${t("withCanvasEletricRoof")}`
        : code === "NBMV"
          ? `${t("small")} ${t("withPanoramicRoof")}`
          : t("small");
    if (MEDIUM.includes(code)) return t("medium");
    if (CABRIO.includes(code)) return t("cabrio");
    if (ELETRIC.includes(code)) return "eletric";
    if (PREMIUM.includes(code)) return t("premium");
    if (MINIVAN.includes(code)) return t("miniVan");
    if (VAN.includes(code)) return t("van");
    return "Unknown category";
  }

  const handleFilterChange = (filterType, value) => {
    setActiveFilters((prevFilters) => {
      const currentValues = prevFilters[filterType] || [];

      // Se já estiver selecionado, remove. Se não, adiciona.
      if (currentValues.includes(value)) {
        return {
          ...prevFilters,
          [filterType]: currentValues.filter((item) => item !== value),
        };
      } else {
        return {
          ...prevFilters,
          [filterType]: [...currentValues, value],
        };
      }
    });
  };

  function checkFuelType(fuel, code) {
    if (code === "NTMV" || code === "CFMV" || code === "CDMV") {
      return t("petrol");
    }

    if (
      code === "DDAE" ||
      code === "HDAE" ||
      code === "CWAE" ||
      code === "DGAE"
    ) {
      return t("eletric");
    }

    return fuel === "Petrol" ? t("petrol") : t("diesel");
  }

  useEffect(() => {
    if (!pageChangedFromOptional) {
      if (choosedCar?.code) {
        const filteredVehicles = vehicles?.filter(
          ({ vehicleCode }) => vehicleCode === choosedCar?.code,
        );

        if (
          filteredVehicles?.length === 0 &&
          vehicles?.length > 0 &&
          !wasModalShowed
        ) {
          setOpenAvaiabilityInformationModal(true);
        } else {
          filteredVehicles?.map(
            ({
              vehicleModel,
              vehicleCode,
              fuelType,
              pictureUrl,
              transmissionType,
              doorCount,
              passengerQuantity,
              totalPrice,
              SCDW,
              GTW,
              extraFees,
              extraEquipments,
              coverages,
            }) =>
              handleReservationOptionals(
                vehicleModel,
                vehicleCode,
                fuelType,
                pictureUrl,
                transmissionType,
                doorCount,
                passengerQuantity,
                totalPrice,
                SCDW,
                GTW,
                extraFees,
                extraEquipments,
                coverages,
                updatedTotalDays,
              ),
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  const handleCloseInformationModal = () => {
    setWasModalShowed(true);
    setOpenInformationModal(false);
  };

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
  };

  const handleCloseAvaiabilityInformationModal = () => {
    setWasModalShowed(true);
    setOpenAvaiabilityInformationModal(false);
  };

  const validateDateReturn = (value, { parent }) => {
    if (!value || !parent.date_pickup) {
      return true;
    }
    return value >= parent.date_pickup;
  };

  const handleUpdateAvaiabilities = () => {
    setUpdateAvaiabilites(true);
  };

  const reservationTimeValidation = yup.object({
    loc_pickup: yup.number().required(t("mandatory")),
    date_pickup: yup
      .date()
      .required(t("mandatory"))
      .min(dayjs().add(2, "day"), t("pickUpDateValidation"))
      .typeError(t("validDate")),
    time_pickup: yup
      .string()
      .required(t("mandatory"))
      .test("time_pickup", t("outsideBusinessHours"), (value) => {
        const hours = parseInt(value.split(":")[0]);
        const minutes = parseInt(value.split(":")[1]);

        return hours * 60 + minutes >= 510 && hours * 60 + minutes <= 1110;
      }),
    loc_return: yup.number().required(t("mandatory")),
    date_return: yup
      .date()
      .required(t("mandatory"))
      .typeError(t("validDate"))
      .test("dateReturn", t("returnDateValidation"), validateDateReturn),
    time_return: yup
      .string()
      .required(t("mandatory"))
      .test("time_return", t("outsideBusinessHours"), (value) => {
        const hours = parseInt(value.split(":")[0]);
        const minutes = parseInt(value.split(":")[1]);

        return hours * 60 + minutes >= 510 && hours * 60 + minutes <= 1110;
      }),
  });

  const [width, setWidth] = React.useState(window.innerWidth);
  const [isLoading, setIsloading] = useState(false);
  const [newPickUpDesk, setNewPickUpDesk] = useState(pickUpDesk);
  const [newDropOffDesk, setNewDropOffDesk] = useState(dropOffDesk);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleChangePickUpDesk = (event) => {
    setValue("loc_pickup", event.target.value, { shouldValidate: true });
    setValue("loc_return", event.target.value, { shouldValidate: true });
    setNewPickUpDesk(event.target.value ?? getValues("loc_pickup"));
    setNewDropOffDesk(event.target.value ?? getValues("loc_return"));
    setPickUpDesk(event.target.value);
    setDropOffDesk(event.target.value);

    if (event.target.value === 47 || event.target.value === 46) {
      setOpenInformationModal(true);
    }
  };

  const handleChangeDropOffDesk = (event) => {
    setValue("loc_return", event.target.value, { shouldValidate: true });
    setNewDropOffDesk(event.target.value || getValues("loc_return"));
    if (event.target.value === 47 || event.target.value === 46) {
      setOpenInformationModal(true);
    }
    setDropOffDesk(event.target.value);
  };

  const handleChangeTimePickup = (time) => {
    if (time?.$m === 0) {
      setValue("time_pickup", `${time?.$H}:${time?.$m}0`, {
        shouldValidate: true,
      });
    } else {
      setValue("time_pickup", `${time?.$H}:${time?.$m}`, {
        shouldValidate: true,
      });
    }
  };

  const handleChangeTimeReturn = (time) => {
    if (time?.$m === 0) {
      setValue("time_return", `${time?.$H}:${time?.$m}0`, {
        shouldValidate: true,
      });
    } else {
      setValue("time_return", `${time?.$H}:${time?.$m}`, {
        shouldValidate: true,
      });
    }
  };

  const handleChangeDatePickup = (date) => {
    setValue("date_pickup", dayjs(date?.$d).format("YYYY-MM-DD"), {
      shouldValidate: true,
    });
  };

  const handleChangeDateReturn = (date) => {
    setValue("date_return", dayjs(date?.$d).format("YYYY-MM-DD"), {
      shouldValidate: true,
    });
  };

  const [error, setError] = React.useState(null);
  const [getAvaiabilities, setAvaiabilities] = useState("");
  const locations = [{ id: 41, name: "Funchal" }];

  const {
    formState: { errors, isValid },
    setValue,
    getValues,
    trigger,
  } = useForm({
    defaultValues: {
      loc_pickup: newPickUpDesk ?? rentalDetails.pickUpDesk,
      date_pickup: reservationInfo?.date_pickup,
      time_pickup: reservationInfo?.time_pickup,
      loc_return: newDropOffDesk ?? rentalDetails.dropOffDesk,
      date_return: reservationInfo?.date_return,
      time_return: reservationInfo?.time_return,
    },
    resolver: yupResolver(reservationTimeValidation),
    reValidateMode: "onChange",
  });

  const calculateTotalDays = (
    pickUpDate,
    pickUpTime,
    returnDate,
    returnTime,
  ) => {
    const pickUpDateTime = dayjs(`${pickUpDate} ${pickUpTime}`);
    const returnDateTime = dayjs(`${returnDate} ${returnTime}`);

    const diffInDays = returnDateTime.diff(pickUpDateTime, "day");

    if (
      returnDateTime.hour() > pickUpDateTime.hour() ||
      (returnDateTime.hour() === pickUpDateTime.hour() &&
        returnDateTime.minute() > pickUpDateTime.minute())
    ) {
      setTotalDays(diffInDays + 1);
      setUpdatedTotalDays(diffInDays + 1);
    } else {
      setTotalDays(diffInDays);
      setUpdatedTotalDays(diffInDays);
    }
  };

  const calculateTotalPrice = (price, days) => {
    const total = (Math.round(price * 100) / 100 / days).toFixed(2);
    return total;
  };

  const fetchAvailabilities = async () => {
    const newAvailabilities = [];

    // Iterate over locations without setting global state values inside the loop
    for (const location of locations) {
      // Construct form data explicitly instead of relying on `setValue`
      const formData = {
        ...getValues(), // Assuming getValues() contains base form data
        loc_pickup: location.id,
        loc_return: location.id,
      };

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: JSON.stringify(formData),
      };

      try {
        const response = await fetch(
          "https://www.whynotcarrental.com/Backend/vehicleAvaibility.php",
          requestOptions,
        );
        const vehicleAvailability = await response.json();

        // Push the specific availability to `newAvailabilities`
        newAvailabilities.push(
          vehicleAvailability.VehAvailRSCore.VehVendorAvails.VehVendorAvail
            .VehAvails,
        );
      } catch (error) {}
    }

    // Set the new state with the fresh `newAvailabilities`
    setAvaiabilities(newAvailabilities);
    return newAvailabilities;
  };

  const types = [
    { label: t("automatic"), value: "Automatic" },
    { label: t("small"), value: "Small" },
    { label: t("medium"), value: "Medium" },
    { label: t("large"), value: "Large" },
    { label: t("suv"), value: "Suv" },
    { label: t("cabrio"), value: "Cabrio" },
    { label: t("eletric"), value: "Eletric" },
    { label: t("miniVan"), value: "MiniVan" },
    { label: t("van"), value: "Van" },
    { label: t("premium"), value: "Premium" },
    { label: t("manual"), value: "Manual" },
    { label: t("diesel"), value: "Diesel" },
    { label: t("petrol"), value: "Petrol" },
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();

    calculateTotalDays(
      getValues("date_pickup"),
      getValues("time_pickup"),
      getValues("date_return"),
      getValues("time_return"),
    );

    await trigger();
    if (Object.keys(errors).length === 0 && isValid) {
      try {
        setIsloading(true);
        const avaiabilities = await fetchAvailabilities(); // Wait for fetchAvailabilities to complete

        setIsloading(false);

        setUpdateAvaiabilites(false);
        setCurrentAvaiabilities(null);
        setCurrentAvaiabilities(avaiabilities);
        setVehicleAvaiabilities(avaiabilities);
        setValue("loc_pickup", newPickUpDesk);
        setValue("loc_return", newDropOffDesk);
        const updatedValues = {
          ...getValues(),
          loc_pickup: newPickUpDesk, // Remove or reset loc_pickup if needed
          loc_return: newDropOffDesk, // Remove or reset loc_return if needed
        };
        setReservationDetails(updatedValues);
      } catch (error) {
        setIsloading(false);
        setError("Network error. Please try again later.");
      }
    } else {
      setOpenErrorModal(true);
      Object.entries(errors).forEach(([field, error]) => {});
    }
  };

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Detect small screens

  const vehicleTypeFilterChecker = (code) => {
    let filtered = "";

    if (AUTOMATIC.includes(code)) {
      filtered += filtered.length ? ", Automatic" : "Automatic";
    }

    if (SMALL.includes(code)) {
      filtered += filtered.length ? ", Small" : "Small";
    }
    if (MEDIUM.includes(code)) {
      filtered += filtered.length ? ", Medium" : "Medium";
    }
    if (BIG.includes(code)) {
      filtered += filtered.length ? ", Large" : "Large";
    }
    if (SUVS.includes(code)) {
      filtered += filtered.length ? ", Suv" : "Suv";
    }
    if (CABRIO.includes(code)) {
      filtered += filtered.length ? ", Cabrio" : "Cabrio";
    }
    if (ELETRIC.includes(code)) {
      filtered += filtered.length ? ", Eletric" : "Eletric";
    }
    if (MINIVAN.includes(code)) {
      filtered += filtered.length ? ", MiniVan" : "MiniVan";
    }
    if (VAN.includes(code)) {
      filtered += filtered.length ? ", Van" : "Van";
    }
    if (PREMIUM.includes(code)) {
      filtered += filtered.length ? ", Premium" : "Premium";
    }
    if (MANUAL.includes(code)) {
      filtered += filtered.length ? ", Manual" : "Manual";
    }
    if (DIESEL.includes(code)) {
      filtered += filtered.length ? ", Diesel" : "Diesel";
    }
    if (PETROL.includes(code)) {
      filtered += filtered.length ? ", Petrol" : "Petrol";
    }

    return filtered || "Unknown category";
  };

  // Lógica de filtragem usando as constantes do Constants.js
  const filteredList = vehicles
    ?.filter((vehicle) => {
      if (!vehicle) return false;
      const code = vehicle.vehicleCode; // O código do carro (ex: "CDAV", "MDMV")

      // 1. Filtro de Transmissão
      if (activeFilters.transmission.length > 0) {
        // Verifica se o código do carro existe em ALGUMA das arrays de constantes selecionadas
        const matchesTransmission = activeFilters.transmission.some(
          (filterVal) => {
            if (filterVal === "Automatic") return AUTOMATIC.includes(code);
            if (filterVal === "Manual") return MANUAL.includes(code);
            return false;
          },
        );
        if (!matchesTransmission) return false;
      }

      // 2. Filtro de Combustível
      if (activeFilters.fuel.length > 0) {
        const matchesFuel = activeFilters.fuel.some((filterVal) => {
          if (filterVal === "Petrol") return PETROL.includes(code);
          if (filterVal === "Diesel") return DIESEL.includes(code);
          // Nota: No teu ficheiro Constants a constante chama-se ELETRIC (sem C)
          if (filterVal === "Electric" || filterVal === "Eletric")
            return ELETRIC.includes(code);
          if (filterVal === "Hybrid") return false; // Adiciona lista HYBRID no Constants.js se tiveres carros híbridos
          return false;
        });
        if (!matchesFuel) return false;
      }

      // 3. Filtro de Categoria
      if (activeFilters.category.length > 0) {
        const matchesCategory = activeFilters.category.some((filterVal) => {
          // Mapeia o valor da checkbox para a Array de códigos correta
          switch (filterVal) {
            case "Small":
              return SMALL.includes(code);
            case "Medium":
              return MEDIUM.includes(code);
            case "Large":
              return BIG.includes(code);
            case "Suv":
              return SUVS.includes(code);
            case "Cabrio":
              return CABRIO.includes(code);
            case "MiniVan":
              return MINIVAN.includes(code);
            case "Van":
              return VAN.includes(code);
            case "Premium":
              return PREMIUM.includes(code);
            case "Scooter":
              return SCOOTERS.includes(code); // Caso tenhas filtro de scooter
            default:
              return false;
          }
        });
        if (!matchesCategory) return false;
      }

      // 4. Manter compatibilidade com o filtro antigo (dropdown no topo, se ainda existir)
      if (vehicleTypeFilter && vehicleTypeFilter.length > 0) {
        const vehicleFeaturesString = vehicleTypeFilterChecker(code);
        const vehicleCategories = vehicleFeaturesString
          .split(", ")
          .map((c) => c.trim());
        const matchesOldFilter = vehicleTypeFilter.every((filter) =>
          vehicleCategories.includes(filter),
        );
        if (!matchesOldFilter) return false;
      }

      return true;
    })
    .sort((a, b) => a.totalPrice - b.totalPrice);

  const getCarImage = (vehicleCode) => {
    const dbVehicle = dbFleet[vehicleCode];
    if (dbVehicle?.picture) return getVehicleImageUrl(dbVehicle);
    const staticVehicle = VEHICLES.find((v) => v.code === vehicleCode);
    if (staticVehicle?.picture) return `${process.env.PUBLIC_URL}/img_car/detail/${staticVehicle.picture}`;
    return null;
  };

  return (
    <ThemeProvider theme={theme}>
      <>
        <div>
          <InformationModal
            open={openAvaiabilityInformationModal}
            handleClose={handleCloseAvaiabilityInformationModal}
            message={t("requestCarUnavaiable")}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={(event) => handleSubmit(event)}>
              <ErrorModal
                open={openErrorModal}
                handleClose={handleCloseErrorModal}
                errors={errors}
              />
              <InformationModal
                open={openInformationModal}
                handleClose={handleCloseInformationModal}
                message={t("hotelDeliveryInfo")}
              />
              <Accordion
                sx={{
                  marginTop: 2,
                  backgroundColor: theme.palette.secondary.main,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="reservationDetails"
                >
                  <Typography color={"white"} variant="h5">
                    {t("reservationDetails")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      paddingTop: 3,
                      paddingBottom: 3,
                      paddingLeft: 2,
                      paddingRight: 2,
                      borderRadius: 4,
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      backgroundColor: "#f9fafc",
                      width: "100%",
                    }}
                  >
                    {isLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <CircularProgress size={200} thickness={2} />
                      </Box>
                    ) : (
                      <>
                        {width > 1200 ? (
                          <>
                            <Grid
                              container
                              justifyContent="space-around"
                              sx={{
                                marginBottom: 1,
                                marginTop: 1,
                                flexDirection: { xs: "column", md: "row" },
                              }}
                            >
                              <Stack
                                alignItems="center"
                                direction="row"
                                gap={1}
                              >
                                <DirectionsCarIcon
                                  sx={{
                                    color: theme.palette.secondary.main,
                                    fontSize: 30,
                                  }}
                                />
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="h5"
                                >
                                  {t("pickUp")}
                                </Typography>
                              </Stack>

                              <Stack
                                alignItems="center"
                                direction="row"
                                gap={1}
                              >
                                <LocationOnIcon
                                  sx={{
                                    color: theme.palette.secondary.main,
                                    fontSize: 30,
                                  }}
                                />
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="h5"
                                >
                                  {t("dropOff")}
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid
                              container
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                flexWrap: {
                                  xs: "wrap",
                                  sm: "wrap",
                                  lg: "nowrap",
                                }, // Wrap on small screens, no wrap on larger
                              }}
                            >
                              <Grid
                                item
                                sx={{ width: { lg: "100%", sm: "auto" } }}
                              >
                                {" "}
                                <FormControl
                                  sx={{ minWidth: { lg: "100%", xl: 230 } }}
                                >
                                  {" "}
                                  <Typography
                                    color={theme.palette.primary.main}
                                    variant="subtitle1"
                                    gutterBottom
                                    id="loc_pickup"
                                  >
                                    {t("location")}
                                  </Typography>
                                  <Select
                                    disabled={!updateAvaiabilities}
                                    labelid="loc_pickup"
                                    id="loc_pickup"
                                    value={getValues("loc_pickup")}
                                    name="loc_pickup"
                                    label={t("deliveryDesk")}
                                    onError={(newError) => setError(newError)}
                                    onChange={handleChangePickUpDesk}
                                  >
                                    {DESKS.map(({ code, name }, index) => (
                                      <MenuItem
                                        label={name}
                                        key={index}
                                        value={code}
                                      >
                                        {name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item sx={{ width: { lg: "100%" } }}>
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="subtitle1"
                                  gutterBottom
                                >
                                  {t("date")}
                                </Typography>
                                <FormControl
                                  sx={{
                                    minWidth: { lg: "100%", xl: 150 },
                                    width: { lg: 150 },
                                  }}
                                >
                                  <DatePicker
                                    disabled={!updateAvaiabilities}
                                    disablePast
                                    name="date_pickup"
                                    format="DD-MM-YYYY"
                                    minDate={dayjs().add(3, "day")}
                                    value={dayjs(getValues("date_pickup"))}
                                    onChange={handleChangeDatePickup}
                                    error={!!errors.date_pickup}
                                    onError={(newError) => setError(newError)}
                                  />
                                </FormControl>
                              </Grid>

                              <Grid
                                item
                                sx={{
                                  width: { lg: "100%" },
                                  marginRight: { sm: 5 },
                                }}
                              >
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="subtitle1"
                                  gutterBottom
                                >
                                  {t("time")}
                                </Typography>
                                <FormControl
                                  sx={{ minWidth: { lg: "100%", xl: 100 } }}
                                >
                                  <TimePicker
                                    disabled={!updateAvaiabilities}
                                    value={dayjs(
                                      getValues("time_pickup"),
                                      "HH:mm",
                                    )}
                                    onChange={handleChangeTimePickup}
                                    ampm={false}
                                    error={!!errors.time_pickup}
                                    onError={(newError) => setError(newError)}
                                  />
                                </FormControl>
                              </Grid>

                              <Grid item sx={{ width: { lg: "100%" } }}>
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="subtitle1"
                                  gutterBottom
                                  id="loc_return"
                                >
                                  {t("location")}
                                </Typography>
                                <FormControl
                                  sx={{ minWidth: { lg: "100%", xl: 250 } }}
                                >
                                  <Select
                                    disabled={!updateAvaiabilities}
                                    required
                                    labelid="loc_return"
                                    id="loc_return"
                                    value={getValues("loc_return")}
                                    name="loc_return"
                                    label={t("returnDesk")}
                                    onChange={handleChangeDropOffDesk}
                                  >
                                    {DESKS.map(({ code, name }, index) => (
                                      <MenuItem
                                        aria-label={name}
                                        key={index}
                                        value={code}
                                      >
                                        {name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item sx={{ width: { lg: "100%" } }}>
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="subtitle1"
                                  gutterBottom
                                >
                                  {t("date")}
                                </Typography>
                                <FormControl
                                  sx={{
                                    minWidth: { lg: "100%", xl: 150 },
                                    width: { lg: 150 },
                                  }}
                                >
                                  <DatePicker
                                    disabled={!updateAvaiabilities}
                                    disablePast
                                    name="date_return"
                                    format="DD-MM-YYYY"
                                    onError={(newError) => setError(newError)}
                                    minDate={dayjs(getValues("date_pickup"))}
                                    value={dayjs(getValues("date_return"))}
                                    onChange={handleChangeDateReturn}
                                    error={!!errors.date_return}
                                  />
                                </FormControl>
                              </Grid>

                              <Grid item sx={{ width: "100%" }}>
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="subtitle1"
                                  gutterBottom
                                >
                                  {t("time")}
                                </Typography>
                                <FormControl
                                  aria-label="time_return"
                                  sx={{ minWidth: "100%" }}
                                >
                                  <TimePicker
                                    disabled={!updateAvaiabilities}
                                    value={dayjs(
                                      getValues("time_return"),
                                      "HH:mm",
                                    )}
                                    onChange={handleChangeTimeReturn}
                                    ampm={false}
                                    onError={(newError) => setError(newError)}
                                    error={!!errors.time_return}
                                  />
                                </FormControl>
                              </Grid>
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid container direction="column" spacing={2}>
                              <Stack
                                alignItems="center"
                                direction="row"
                                gap={1}
                                sx={{ marginBottom: 1 }}
                              >
                                <DirectionsCarIcon
                                  sx={{
                                    color: theme.palette.secondary.main,
                                    fontSize: 30,
                                  }}
                                />
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="h5"
                                  sx={{
                                    "@media (max-width: 610px)": {
                                      fontSize: "1.2rem", // Reduce font size on smaller screens
                                    },
                                  }}
                                >
                                  {t("pickUp")}
                                </Typography>
                              </Stack>

                              <Grid
                                container
                                spacing={1}
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  flexWrap: "nowrap", // Default behavior
                                  "@media (max-width: 610px)": {
                                    flexWrap: "wrap", // Stack items vertically on small screens
                                  },
                                }}
                              >
                                <Grid item>
                                  <FormControl
                                    sx={{
                                      minWidth: 250,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                      labelid="loc_pickup"
                                    >
                                      {t("location")}
                                    </Typography>
                                    <Select
                                      disabled={!updateAvaiabilities}
                                      labelid="loc_pickup"
                                      id="loc_pickup"
                                      value={getValues("loc_pickup")}
                                      name="loc_pickup"
                                      label={t("deliveryDesk")}
                                      onError={(newError) => setError(newError)}
                                      onChange={handleChangePickUpDesk}
                                    >
                                      {DESKS.map(({ code, name }, index) => (
                                        <MenuItem
                                          aria-label={name}
                                          key={index}
                                          value={code}
                                        >
                                          {name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                <Grid item>
                                  <Typography variant="subtitle1" gutterBottom>
                                    {t("date")}
                                  </Typography>
                                  <FormControl
                                    size="medium"
                                    sx={{
                                      "@media (max-width: 610px)": {
                                        minWidth: 250,
                                      },
                                    }}
                                  >
                                    <DatePicker
                                      disabled={!updateAvaiabilities}
                                      disablePast
                                      name="date_pickup"
                                      format="DD-MM-YYYY"
                                      minDate={dayjs().add(3, "day")}
                                      value={dayjs(getValues("date_pickup"))}
                                      onChange={handleChangeDatePickup}
                                      error={!!errors.date_pickup}
                                      onError={(newError) => setError(newError)}
                                    />
                                  </FormControl>
                                </Grid>

                                <Grid item>
                                  <Typography variant="subtitle1" gutterBottom>
                                    {t("time")}
                                  </Typography>
                                  <FormControl
                                    size="medium"
                                    sx={{
                                      "@media (max-width: 610px)": {
                                        minWidth: 250,
                                      },
                                    }}
                                  >
                                    <TimePicker
                                      disabled={!updateAvaiabilities}
                                      value={dayjs(
                                        getValues("time_pickup"),
                                        "HH:mm",
                                      )}
                                      onChange={handleChangeTimePickup}
                                      ampm={false}
                                      error={!!errors.time_pickup}
                                      onError={(newError) => setError(newError)}
                                    />
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid
                              container
                              direction="column"
                              spacing={2}
                              sx={{ marginTop: 4 }}
                            >
                              <Stack
                                alignItems="center"
                                direction="row"
                                gap={1}
                                sx={{ marginBottom: 1 }}
                              >
                                <LocationOnIcon
                                  sx={{
                                    color: theme.palette.secondary.main,
                                    fontSize: 30,
                                  }}
                                />
                                <Typography
                                  color={theme.palette.primary.main}
                                  variant="h5"
                                  sx={{
                                    "@media (max-width: 610px)": {
                                      fontSize: "1.2rem", // Reduce font size on small screens
                                    },
                                  }}
                                >
                                  {t("dropOff")}
                                </Typography>
                              </Stack>
                              <Grid
                                container
                                spacing={1}
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  flexWrap: "nowrap", // Default behavior
                                  "@media (max-width: 610px)": {
                                    flexWrap: "wrap", // Stack items vertically on small screens
                                  },
                                }}
                              >
                                <Grid item>
                                  <FormControl
                                    sx={{
                                      minWidth: 250,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                      id="loc_return"
                                    >
                                      {t("location")}
                                    </Typography>
                                    <Select
                                      disabled={!updateAvaiabilities}
                                      labelid="loc_return"
                                      id="loc_return"
                                      value={getValues("loc_return")}
                                      name="loc_return"
                                      label={t("returnDesk")}
                                      onChange={handleChangeDropOffDesk}
                                    >
                                      {DESKS.map(({ code, name }, index) => (
                                        <MenuItem
                                          aria-label={name}
                                          key={index}
                                          value={code}
                                        >
                                          {name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                <Grid item>
                                  <Typography variant="subtitle1" gutterBottom>
                                    {t("date")}
                                  </Typography>
                                  <FormControl
                                    size="medium"
                                    sx={{
                                      "@media (max-width: 610px)": {
                                        minWidth: 250,
                                      },
                                    }}
                                  >
                                    <DatePicker
                                      disabled={!updateAvaiabilities}
                                      disablePast
                                      name="date_return"
                                      format="DD-MM-YYYY"
                                      onError={(newError) => setError(newError)}
                                      minDate={dayjs(getValues("date_pickup"))}
                                      value={dayjs(getValues("date_return"))}
                                      onChange={handleChangeDateReturn}
                                      error={!!errors.date_return}
                                    />
                                  </FormControl>
                                </Grid>

                                <Grid item>
                                  <Typography variant="subtitle1" gutterBottom>
                                    {t("time")}
                                  </Typography>
                                  <FormControl
                                    size="medium"
                                    sx={{
                                      "@media (max-width: 610px)": {
                                        minWidth: 250,
                                      },
                                    }}
                                  >
                                    <TimePicker
                                      disabled={!updateAvaiabilities}
                                      value={dayjs(
                                        getValues("time_return"),
                                        "HH:mm",
                                      )}
                                      onChange={handleChangeTimeReturn}
                                      ampm={false}
                                      onError={(newError) => setError(newError)}
                                      error={!!errors.time_return}
                                    />
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Grid>
                          </>
                        )}
                        {updateAvaiabilities ? (
                          <Grid
                            item
                            sx={{
                              textAlign: { lg: "center", xl: "right" },
                              width: "100%",
                            }}
                          >
                            <Button
                              type="submit"
                              value="Send"
                              variant="contained"
                              endIcon={<SendIcon />}
                              size="large"
                              disabled={isLoading}
                              sx={{
                                marginTop: 2,
                              }}
                            >
                              <input
                                type="hidden"
                                name="action"
                                value="GetVehAvailRate"
                              ></input>
                              {t("search")}
                            </Button>
                          </Grid>
                        ) : (
                          <Button
                            type="submit"
                            value="updateAvaiabilities"
                            variant="contained"
                            endIcon={<SendIcon />}
                            size="large"
                            sx={{
                              marginTop: 2,
                            }}
                            onClick={handleUpdateAvaiabilities}
                          >
                            {t("changeDates")}
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </form>
          </LocalizationProvider>
        </div>
        <div>
          <Box
            sx={{
              display: "flex",
              // 1. Em mobile (xs) fica coluna, Desktop (sm) fica linha
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              mt: 2,
              mb: 2,
              px: 2,
              // 2. Adiciona espaço vertical entre o título e o botão em mobile
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography
              variant="h4"
              component="div"
              sx={{
                color: "primary.main",
                textAlign: "center",
              }}
            >
              {t("fleet")}
            </Typography>

            {/* Botão Filtros - Só aparece se width < 1130 */}
            {width < 1130 && (
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setIsFilterModalOpen(true)}
                sx={{
                  position: { xs: "relative", sm: "absolute" },
                  right: { xs: "auto", sm: 16 },
                  top: { xs: "auto", sm: "50%" },
                  transform: { xs: "none", sm: "translateY(-50%)" },
                  width: { xs: "100%", sm: "auto" },
                  maxWidth: { xs: "300px", sm: "none" }, // Impede que fique gigante em tablets pequenos
                }}
              >
                {t("filters")}
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 3,
            }}
          >
            {/* FilterSidebar on the left */}
            {width > 1130 ? (
              <Box sx={{ width: "300px" }}>
                <FiltersSidebar
                  activeFilters={activeFilters}
                  onFilterChange={handleFilterChange}
                  // Opcional: passar contagens de carros disponíveis para mostrar na sidebar
                  vehicles={vehicles}
                  onClear={handleClearFilters}
                />
              </Box>
            ) : null}

            {/* Vehicle Cards on the right */}
            <Box sx={{ width: "100%" }}>
              <div>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end", // Align content to the right
                    width: "100%",
                  }}
                >
                  {/* <div
                    style={{
                      padding: 0,
                      width: isSmallScreen ? "100%" : null,
                    }}
                  >
                    <CarCategorySelector
                      selected={selectedCategory}
                      onSelect={setSelectedCategory}
                    />
                    {width < 1130 ? (
                      <Box sx={{ width: "300px" }}>
                        <FiltersDropdown />
                      </Box>
                    ) : null}
                  </div> */}
                </Box>
                <div>
                  <Container
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      width: "100%",
                      gap: 2, // Add gap between cards
                    }}
                  >
                    {vehicles ? (
                      (() => {
                        const filteredVehicles = vehicles
                          .filter((vehicle) => {
                            // Obter o código do veículo (Ex: "MDMV")
                            const code = vehicle.vehicleCode;

                            // 1. FILTRO DE COMBUSTÍVEL
                            // Verifica se o código do carro está nas listas PETROL, DIESEL, ELETRIC importadas
                            if (activeFilters.fuel.length > 0) {
                              const matchesFuel = activeFilters.fuel.some(
                                (filterVal) => {
                                  if (filterVal === "Petrol")
                                    return PETROL.includes(code);
                                  if (filterVal === "Diesel")
                                    return DIESEL.includes(code);
                                  if (filterVal === "Electric")
                                    return ELETRIC.includes(code); // Nota: ELETRIC sem 'C' conforme o teu Constants.js
                                  if (filterVal === "Hybrid") return false; // Adicionar lista HYBRID se existirem no futuro
                                  return false;
                                },
                              );
                              if (!matchesFuel) return false;
                            }

                            // 2. FILTRO DE TRANSMISSÃO
                            // Verifica se o código está nas listas AUTOMATIC ou MANUAL
                            if (activeFilters.transmission.length > 0) {
                              const matchesTransmission =
                                activeFilters.transmission.some((filterVal) => {
                                  if (filterVal === "Automatic")
                                    return AUTOMATIC.includes(code);
                                  if (filterVal === "Manual")
                                    return MANUAL.includes(code);
                                  return false;
                                });
                              if (!matchesTransmission) return false;
                            }

                            // 3. FILTRO DE CATEGORIA
                            // Mapeia o nome do filtro para a lista de Constantes correta (SMALL, SUVS, BIG, etc)
                            if (activeFilters.category.length > 0) {
                              const matchesCategory =
                                activeFilters.category.some((filterVal) => {
                                  switch (filterVal) {
                                    case "Small":
                                      return SMALL.includes(code);
                                    case "Medium":
                                      return MEDIUM.includes(code);
                                    case "Large":
                                      return BIG.includes(code);
                                    case "Suv":
                                      return SUVS.includes(code);
                                    case "Cabrio":
                                      return CABRIO.includes(code);
                                    case "MiniVan":
                                      return MINIVAN.includes(code);
                                    case "Van":
                                      return VAN.includes(code);
                                    case "Premium":
                                      return PREMIUM.includes(code);
                                    case "Electric":
                                      return ELETRIC.includes(code);
                                    default:
                                      return false;
                                  }
                                });
                              if (!matchesCategory) return false;
                            }

                            // 4. MANTER FILTRO ANTIGO (Dropdown do topo)
                            if (
                              vehicleTypeFilter &&
                              vehicleTypeFilter.length > 0
                            ) {
                              const vehicleFeaturesString =
                                vehicleTypeFilterChecker(code);
                              const vehicleCategories = vehicleFeaturesString
                                .split(", ")
                                .map((c) => c.trim());

                              const matchesOldFilter = vehicleTypeFilter.every(
                                (filter) => vehicleCategories.includes(filter),
                              );
                              if (!matchesOldFilter) return false;
                            }

                            return true;
                          })
                          .sort((a, b) => a.totalPrice - b.totalPrice);

                        return filteredVehicles.length === 0 ? (
                          <Typography variant="h5">
                            {t("noFilteredCarsAvaiable")}
                          </Typography>
                        ) : (
                          filteredVehicles?.map(
                            (
                              {
                                vehicleModel,
                                vehicleCode,
                                fuelType,
                                pictureUrl,
                                transmissionType,
                                doorCount,
                                passengerQuantity,
                                totalPrice,
                                SCDW,
                                GTW,
                                extraFees,
                                extraEquipments,
                                coverages,
                              },
                              index,
                            ) => (
                              <Box
                                key={index}
                                sx={{
                                  padding: 0,
                                  width: isSmallScreen ? "100%" : "auto",
                                }}
                              >
                                {isLoading ? (
                                  <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={200}
                                  />
                                ) : vehicleCode ? (
                                  <Card
                                    key={index}
                                    sx={{
                                      display: "flex",
                                      flexDirection: isSmallScreen ? "column" : "row",
                                      alignItems: "stretch",
                                      borderRadius: 3,
                                      overflow: "hidden",
                                      mb: 2,
                                      width: isSmallScreen ? "100%" : 820,
                                      boxShadow: "0 2px 16px rgba(28,37,65,0.08)",
                                      border: "1px solid #e8eaf0",
                                    }}
                                  >
                                    {/* Image */}
                                    <Box
                                      sx={{
                                        position: "relative",
                                        width: isSmallScreen ? "100%" : 210,
                                        bgcolor: "#f8f9fb",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        p: 1,
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={getCarImage(vehicleCode)}
                                        alt={vehicleModel}
                                        sx={{
                                          width: "100%",
                                          height: isSmallScreen ? 150 : 130,
                                          objectFit: "contain",
                                        }}
                                      />
                                      <Chip
                                        label={`${t("group")} ${vehicleCodeChanger(vehicleCode)}`}
                                        size="small"
                                        sx={{
                                          position: "absolute",
                                          top: 8,
                                          left: 8,
                                          bgcolor: "#1C2541",
                                          color: "#fff",
                                          fontWeight: "bold",
                                          fontSize: 10,
                                        }}
                                      />
                                    </Box>

                                    {/* Vehicle info */}
                                    <Box
                                      sx={{
                                        flex: 1,
                                        p: 2,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                                        {dbFleet[vehicleCode]?.brand || vehicleModel}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                                        {t("orSimilar")}
                                      </Typography>

                                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                        <Tooltip title={t("fuelType")}>
                                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, minWidth: 44 }}>
                                            <img width="20" height="20" src="https://img.icons8.com/material/24/gas-station--v1.png" alt="fuel" />
                                            <Typography variant="caption" align="center">
                                              {checkFuelType(dbFleet[vehicleCode]?.fuelType || fuelType, vehicleCode)}
                                            </Typography>
                                          </Box>
                                        </Tooltip>

                                        <Tooltip title={t("gearbox")}>
                                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, minWidth: 44 }}>
                                            <img width="20" height="20" src="https://img.icons8.com/material/24/gearbox-selector.png" alt="gearbox" />
                                            <Typography variant="caption" align="center">
                                              {dbFleet[vehicleCode]?.transmission
                                                ? (dbFleet[vehicleCode].transmission === "Automatic" ? t("automatic") : t("manual"))
                                                : (AUTOMATIC.includes(vehicleCode) ? t("automatic") : t("manual"))}
                                            </Typography>
                                          </Box>
                                        </Tooltip>

                                        <Tooltip title={t("doors")}>
                                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, minWidth: 44 }}>
                                            <img width="20" height="20" src="https://img.icons8.com/material/24/car-door--v2.png" alt="doors" />
                                            <Typography variant="caption" align="center">
                                              {dbFleet[vehicleCode]?.doors ?? doorCount}
                                            </Typography>
                                          </Box>
                                        </Tooltip>

                                        <Tooltip title={t("seats")}>
                                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, minWidth: 44 }}>
                                            <img width="20" height="20" src="https://img.icons8.com/material/24/aircraft-seat-middle.png" alt="seats" />
                                            <Typography variant="caption" align="center">
                                              {dbFleet[vehicleCode]?.seats ?? passengerQuantity} {t("seats")}
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                      </Stack>

                                      {vehicleCode === "DDAD" && (
                                        <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                                          <img width="16" height="16" src="https://img.icons8.com/material/24/info--v1.png" alt="info" />
                                          <Typography variant="caption" color="text.secondary">
                                            {t("mandatoryExcess")}
                                          </Typography>
                                        </Stack>
                                      )}
                                    </Box>

                                    {/* Price + CTA */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        p: 2.5,
                                        borderLeft: isSmallScreen ? "none" : "1px solid #e8eaf0",
                                        borderTop: isSmallScreen ? "1px solid #e8eaf0" : "none",
                                        minWidth: 150,
                                        bgcolor: isSmallScreen ? "transparent" : "#fafbfc",
                                      }}
                                    >
                                      <Typography variant="caption" color="text.secondary">{t("from")}</Typography>
                                      <Typography variant="h4" color="primary.main" fontWeight="bold" lineHeight={1.1}>
                                        {calculateTotalPrice(
                                          totalPrice,
                                          updatedTotalDays === 0 ? totalDays : updatedTotalDays,
                                        )}€
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                                        /{t("day")}
                                      </Typography>
                                      <Button
                                        variant="contained"
                                        color="success"
                                        sx={{ borderRadius: 2, fontWeight: "bold", px: 2, width: "100%" }}
                                        onClick={() =>
                                          handleReservationOptionals(
                                            vehicleModel,
                                            vehicleCode,
                                            fuelType,
                                            pictureUrl,
                                            transmissionType,
                                            doorCount,
                                            passengerQuantity,
                                            totalPrice,
                                            SCDW,
                                            GTW,
                                            extraFees,
                                            extraEquipments,
                                            coverages,
                                            updatedTotalDays,
                                          )
                                        }
                                      >
                                        {t("viewDeal")}
                                      </Button>
                                    </Box>
                                  </Card>
                                ) : null}
                              </Box>
                            ),
                          )
                        );
                      })()
                    ) : (
                      <p>{t("noCarsAvaiable")}</p>
                    )}
                  </Container>
                </div>
              </div>
            </Box>
          </Box>
        </div>
        {/* --- NOVO: DIALOG (POP-UP) PARA FILTROS MOBILE --- */}
        <Dialog
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          fullWidth
          maxWidth="xs" // Tamanho estreito tipo mobile
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {t("filters")}
            <IconButton onClick={() => setIsFilterModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {/* Reutiliza a mesma componente FiltersSidebar */}
            <FiltersSidebar
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              vehicles={vehicles}
              onClear={handleClearFilters}
              isMobile={true}
            />
          </DialogContent>
        </Dialog>
      </>
    </ThemeProvider>
  );
}

ChooseVehicle.propTypes = {
  reservationInfo: PropTypes.any,
  avaiabilities: PropTypes.array,
  pickUpDesk: PropTypes.number,
  dropOffDesk: PropTypes.number,
  totalDays: PropTypes.number,
  choosedCar: PropTypes.any,
  wasCarChoosed: PropTypes.any,
  pageChangedFromOptional: PropTypes.any,
  setTotalDays: PropTypes.any,
  setReservationDetails: PropTypes.any,
  setVehicleAvaiabilities: PropTypes.any,
  setPickUpDesk: PropTypes.any,
  setDropOffDesk: PropTypes.any,
};

export default ChooseVehicle;
